import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

export const ContactForm = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Crear mensaje para WhatsApp directamente con los datos del formulario
    const whatsappMessage = `*Nuevo mensaje de contacto*

*Nombre:* ${formData.name}
*Email:* ${formData.email}
*Asunto:* ${formData.subject}

*Mensaje:*
${formData.message}`;

    // Construir URL de WhatsApp usando api.whatsapp.com para abrir app nativa
    const phoneNumber = '34623154054';
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
    
    // Redirigir directamente (abre la app nativa)
    window.location.href = whatsappUrl;

    // Mostrar confirmación
    toast({
      title: t('contact.toastTitle'),
      description: t('contact.toastDescription'),
    });

    // Limpiar formulario
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSubmitting(false);
  };

  return (
    <section id="contact-section" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          {/* Section Header */}
          <div className="mb-12">
            <div className="flex flex-col items-center mb-4">
              {/* Profile Image - Centered and 50% larger */}
              <img 
                src="/api/static/brand/joan-aravell.jpg"
                alt="Joan Monzó"
                className="w-36 h-36 md:w-48 md:h-48 object-cover rounded-full shadow-lg mb-6"
              />
              {/* Title */}
              <h2 className="font-serif text-3xl md:text-5xl font-bold text-slate-800">
                {t('contact.title')}
              </h2>
            </div>
            <div className="h-1 w-24 bg-emerald-600 mx-auto mb-6"></div>
            <p className="text-lg text-slate-600 text-center">
              {t('contact.subtitle')}
            </p>
          </div>

          {/* Contact Form Card */}
          <Card className="border-2 border-slate-200 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-slate-800">
                {t('contact.cardTitle')}
              </CardTitle>
              <CardDescription>
                {t('contact.cardDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.name')} *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.namePlaceholder')}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.email')} *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.emailPlaceholder')}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.subject')} *
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t('contact.subjectPlaceholder')}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.message')} *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.messagePlaceholder')}
                    rows={6}
                    className="w-full resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold"
                >
                  {isSubmitting ? t('contact.submitting') : t('contact.submitButton')}
                  <Send size={20} className="ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};